import React, { useRef, useMemo, Suspense } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { OrbitControls, Html } from '@react-three/drei'
import * as THREE from 'three'

interface DoorProps {
  position: [number, number, number]
  label: string
  isSelected: boolean
  isCorrect: boolean | null
  showFeedback: boolean
  onClick: () => void
}

interface CharacterProps {
  isMovingToDoor?: boolean
  targetDoorIndex?: number
  isTransitioning?: boolean
  roomOffset?: number
}

/**
 * Component nhân vật hiện đại với nhiều chi tiết
 */
const Character: React.FC<CharacterProps> = ({ 
  isMovingToDoor = false, 
  targetDoorIndex,
  isTransitioning = false,
  roomOffset = 0
}) => {
  const meshRef = useRef<THREE.Group>(null)
  const headRef = useRef<THREE.Group>(null)
  const leftArmRef = useRef<THREE.Group>(null)
  const rightArmRef = useRef<THREE.Group>(null)
  const leftLegRef = useRef<THREE.Group>(null)
  const rightLegRef = useRef<THREE.Group>(null)

  useFrame(({ clock }) => {
    const time = clock.getElapsedTime()
    
    if (meshRef.current) {
      if (isTransitioning && targetDoorIndex !== undefined) {
        // CHUYỂN PHÒNG - CHẬM VÀ MƯỢT: 
        // 1. Đi GIỮA cửa được chọn (căn chỉnh X chính xác)
        // 2. Đi QUA cửa và dọc theo phòng (chậm rãi)
        // 3. Tới cuối phòng gặp 3 cửa mới
        
        const doorX = (targetDoorIndex - 1) * 7.5 // Vị trí X CHÍNH GIỮA cửa (-7.5, 0, 7.5)
        const doorZ = roomOffset - 3.5 // Vị trí Z của cửa
        const endRoomZ = roomOffset - 32 // Cuối phòng (gần cửa tiếp theo)
        
        // Bước 1: Căn chỉnh X NHANH để đi CHÍNH GIỮA cửa
        meshRef.current.position.x = THREE.MathUtils.lerp(meshRef.current.position.x, doorX, 0.2)
        
        // Bước 2: Đi thẳng qua cửa theo Z (chậm để mượt mà)
        meshRef.current.position.z = THREE.MathUtils.lerp(meshRef.current.position.z, endRoomZ, 0.03)
        
        // Luôn quay mặt về phía trước
        meshRef.current.rotation.y = Math.PI
        
        // Đi bộ vừa phải
        const legSwing = Math.sin(time * 7) * 0.5
        if (leftLegRef.current && rightLegRef.current) {
          leftLegRef.current.rotation.x = legSwing
          rightLegRef.current.rotation.x = -legSwing
        }
      } else if (isMovingToDoor && targetDoorIndex !== undefined) {
        // Di chuyển ĐẾN GIỮA cửa được chọn - CHẬM LẠI
        const doorX = (targetDoorIndex - 1) * 7.5 // GIỮA cửa (-7.5, 0, 7.5)
        const targetZ = roomOffset - 7 // Dừng xa cửa hơn để không chui qua
        
        // Di chuyển X NHANH để căn chỉnh chính xác, Z chậm
        meshRef.current.position.x = THREE.MathUtils.lerp(meshRef.current.position.x, doorX, 0.15)
        meshRef.current.position.z = THREE.MathUtils.lerp(meshRef.current.position.z, targetZ, 0.04)
        
        // Quay mặt về cửa
        meshRef.current.rotation.y = Math.PI
        
        // Đi bộ chậm
        const legSwing = Math.sin(time * 5) * 0.4
        if (leftLegRef.current && rightLegRef.current) {
          leftLegRef.current.rotation.x = legSwing
          rightLegRef.current.rotation.x = -legSwing
        }
      } else {
        // Đi bộ chậm trong phòng hiện tại
        const xPos = Math.sin(time * 0.4) * 6
        // Di chuyển trong phòng hiện tại - gần đầu phòng
        const baseZ = roomOffset + 1 + Math.sin(time * 0.2) * 0.5
        meshRef.current.position.x = xPos
        meshRef.current.position.z = baseZ
        
        // Luôn quay mặt về 3 cánh cửa (phía trước)
        meshRef.current.rotation.y = Math.PI
      }
      
      // Nhún nhẹ khi đi bộ
      meshRef.current.position.y = 0.5 + Math.abs(Math.sin(time * 3)) * 0.06
    }

    if (headRef.current) {
      // Đầu xoay nhẹ trái phải khi đi
      headRef.current.rotation.y = Math.sin(time * 1.2) * 0.15
    }

    // Animation chân đi bộ
    if (leftLegRef.current && rightLegRef.current) {
      const legSwing = Math.sin(time * 3) * 0.35
      leftLegRef.current.rotation.x = legSwing
      rightLegRef.current.rotation.x = -legSwing
    }

    // Animation tay đi bộ - Lệch ra ngoài nhẹ
    if (leftArmRef.current && rightArmRef.current) {
      const armSwing = Math.sin(time * 3) * 0.5
      leftArmRef.current.rotation.x = -armSwing
      rightArmRef.current.rotation.x = armSwing
      leftArmRef.current.rotation.z = -0.15  // Lệch ra ngoài nhẹ
      rightArmRef.current.rotation.z = 0.15  // Lệch ra ngoài nhẹ
    }
  })

  return (
    <group ref={meshRef} position={[0, 0.5, roomOffset + 2]} rotation={[0, Math.PI, 0]} scale={1.4}>
      {/* === ĐẦU === */}
      <group ref={headRef} position={[0, 0.9, 0]}>
        {/* Đầu chính */}
        <mesh position={[0, 0, 0]}>
          <sphereGeometry args={[0.32, 32, 32]} />
          <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
        </mesh>

        {/* Tóc */}
        <mesh position={[0, 0.15, 0]}>
          <sphereGeometry args={[0.33, 32, 32, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshStandardMaterial color="#2d1810" roughness={0.9} />
        </mesh>

        {/* Mắt trái - trắng */}
        <mesh position={[-0.12, 0.08, 0.28]}>
          <sphereGeometry args={[0.06, 16, 16]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>

        {/* Mắt trái - đen */}
        <mesh position={[-0.12, 0.08, 0.33]}>
          <sphereGeometry args={[0.035, 16, 16]} />
          <meshStandardMaterial color="#000000" />
        </mesh>

        {/* Mắt phải - trắng */}
        <mesh position={[0.12, 0.08, 0.28]}>
          <sphereGeometry args={[0.06, 16, 16]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>

        {/* Mắt phải - đen */}
        <mesh position={[0.12, 0.08, 0.33]}>
          <sphereGeometry args={[0.035, 16, 16]} />
          <meshStandardMaterial color="#000000" />
        </mesh>

        {/* Chấm sáng mắt trái */}
        <mesh position={[-0.11, 0.09, 0.36]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={0.5} />
        </mesh>

        {/* Chấm sáng mắt phải */}
        <mesh position={[0.13, 0.09, 0.36]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={0.5} />
        </mesh>

        {/* Miệng cười */}
        <mesh position={[0, -0.08, 0.28]} rotation={[0, 0, 0]}>
          <torusGeometry args={[0.12, 0.025, 16, 32, Math.PI]} />
          <meshStandardMaterial color="#ff6b9d" roughness={0.6} />
        </mesh>

        {/* Mũi */}
        <mesh position={[0, 0.02, 0.32]}>
          <coneGeometry args={[0.04, 0.08, 16]} />
          <meshStandardMaterial color="#ffc896" roughness={0.7} />
        </mesh>
      </group>

      {/* === CỔ === */}
      <mesh position={[0, 0.55, 0]}>
        <cylinderGeometry args={[0.12, 0.12, 0.15, 16]} />
        <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
      </mesh>

      {/* === THÂN === */}
      {/* Áo thun */}
      <mesh position={[0, 0.15, 0]}>
        <cylinderGeometry args={[0.25, 0.3, 0.65, 32]} />
        <meshStandardMaterial color="#10b981" roughness={0.8} />
      </mesh>

      {/* Logo trên áo */}
      <mesh position={[0, 0.3, 0.25]}>
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshStandardMaterial color="#fbbf24" emissive="#fbbf24" emissiveIntensity={0.3} />
      </mesh>

      {/* === TAY TRÁI === */}
      <group ref={leftArmRef} position={[-0.25, 0.3, 0.18]}>
        {/* Vai */}
        <mesh position={[0, 0, 0]}>
          <sphereGeometry args={[0.1, 16, 16]} />
          <meshStandardMaterial color="#10b981" roughness={0.8} />
        </mesh>
        
        {/* Cánh tay - Thẳng xuống */}
        <mesh position={[0, -0.25, 0]}>
          <cylinderGeometry args={[0.07, 0.06, 0.5, 16]} />
          <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
        </mesh>

        {/* Bàn tay */}
        <mesh position={[0, -0.52, 0]}>
          <sphereGeometry args={[0.08, 16, 16]} />
          <meshStandardMaterial color="#ffc896" roughness={0.7} />
        </mesh>
      </group>

      {/* === TAY PHẢI === */}
      <group ref={rightArmRef} position={[0.25, 0.3, 0.18]}>
        {/* Vai */}
        <mesh position={[0, 0, 0]}>
          <sphereGeometry args={[0.1, 16, 16]} />
          <meshStandardMaterial color="#10b981" roughness={0.8} />
        </mesh>
        
        {/* Cánh tay - Thẳng xuống */}
        <mesh position={[0, -0.25, 0]}>
          <cylinderGeometry args={[0.07, 0.06, 0.5, 16]} />
          <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
        </mesh>

        {/* Bàn tay */}
        <mesh position={[0, -0.52, 0]}>
          <sphereGeometry args={[0.08, 16, 16]} />
          <meshStandardMaterial color="#ffc896" roughness={0.7} />
        </mesh>
      </group>

      {/* === CHÂN TRÁI === */}
      <group ref={leftLegRef} position={[-0.12, -0.25, 0]}>
        {/* Quần */}
        <mesh position={[0, 0.05, 0]}>
          <cylinderGeometry args={[0.11, 0.09, 0.35, 16]} />
          <meshStandardMaterial color="#3b82f6" roughness={0.8} />
        </mesh>
        
        {/* Chân */}
        <mesh position={[0, -0.25, 0]}>
          <cylinderGeometry args={[0.09, 0.08, 0.35, 16]} />
          <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
        </mesh>

        {/* Giày */}
        <mesh position={[0, -0.48, 0.06]}>
          <boxGeometry args={[0.14, 0.1, 0.22]} />
          <meshStandardMaterial color="#1f2937" roughness={0.5} />
        </mesh>
      </group>

      {/* === CHÂN PHẢI === */}
      <group ref={rightLegRef} position={[0.12, -0.25, 0]}>
        {/* Quần */}
        <mesh position={[0, 0.05, 0]}>
          <cylinderGeometry args={[0.11, 0.09, 0.35, 16]} />
          <meshStandardMaterial color="#3b82f6" roughness={0.8} />
        </mesh>
        
        {/* Chân */}
        <mesh position={[0, -0.25, 0]}>
          <cylinderGeometry args={[0.09, 0.08, 0.35, 16]} />
          <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
        </mesh>

        {/* Giày */}
        <mesh position={[0, -0.48, 0.06]}>
          <boxGeometry args={[0.14, 0.1, 0.22]} />
          <meshStandardMaterial color="#1f2937" roughness={0.5} />
        </mesh>
      </group>

      {/* === BACKPACK === */}
      <mesh position={[0, 0.2, -0.22]}>
        <boxGeometry args={[0.3, 0.4, 0.15]} />
        <meshStandardMaterial color="#8b5cf6" roughness={0.8} />
      </mesh>

      {/* Dây backpack trái */}
      <mesh position={[-0.1, 0.35, -0.05]} rotation={[0.2, 0, 0]}>
        <cylinderGeometry args={[0.03, 0.03, 0.35, 16]} />
        <meshStandardMaterial color="#6d28d9" roughness={0.8} />
      </mesh>

      {/* Dây backpack phải */}
      <mesh position={[0.1, 0.35, -0.05]} rotation={[0.2, 0, 0]}>
        <cylinderGeometry args={[0.03, 0.03, 0.35, 16]} />
        <meshStandardMaterial color="#6d28d9" roughness={0.8} />
      </mesh>
    </group>
  )
}

/**
 * Component cho một cánh cửa 3D
 */
const Door: React.FC<DoorProps> = ({ position, label, isSelected, isCorrect, showFeedback, onClick }) => {
  const doorGroupRef = useRef<THREE.Group>(null)
  const [hovered, setHovered] = React.useState(false)

  // Animation mở cửa - quay group quanh trục tại cạnh trái
  useFrame(() => {
    if (doorGroupRef.current) {
      // Đảm bảo scale luôn là 1 (không thay đổi kích thước)
      doorGroupRef.current.scale.set(1, 1, 1)
      
      // Hiệu ứng mở cửa khi đúng - Quay cùng chiều kim đồng hồ để mở ra ngoài
      if (showFeedback && isCorrect) {
        doorGroupRef.current.rotation.y = THREE.MathUtils.lerp(doorGroupRef.current.rotation.y, -Math.PI / 2, 0.06)
      } else {
        // Reset về vị trí ban đầu
        doorGroupRef.current.rotation.y = THREE.MathUtils.lerp(doorGroupRef.current.rotation.y, 0, 0.1)
      }
    }
  })

  // Màu sắc cửa dựa trên trạng thái
  const doorColor = useMemo(() => {
    if (showFeedback) {
      if (isSelected && isCorrect) return '#10b981' // Xanh lá - đúng
      if (isSelected && !isCorrect) return '#ef4444' // Đỏ - sai
    }
    return hovered ? '#fbbf24' : '#8b5cf6' // Vàng sáng hover, tím mặc định
  }, [showFeedback, isSelected, isCorrect, hovered])

  return (
    <group position={position}>
      {/* Khung cửa */}
      <mesh position={[0, 0, -0.05]}>
        <boxGeometry args={[5, 7, 0.15]} />
        <meshStandardMaterial color="#4a044e" />
      </mesh>

      {/* Group quay - Pivot ở cạnh trái cửa */}
      <group ref={doorGroupRef} position={[-2.4, 0, 0]}>
        {/* Cánh cửa - offset sang phải để pivot ở cạnh trái */}
        <mesh
          position={[2.4, 0, 0]}
          onClick={onClick}
          onPointerOver={() => !showFeedback && setHovered(true)}
          onPointerOut={() => setHovered(false)}
        >
          <boxGeometry args={[4.8, 6.8, 0.2]} />
          <meshStandardMaterial 
            color={doorColor}
            metalness={0.5}
            roughness={0.3}
            emissive={doorColor}
            emissiveIntensity={hovered ? 0.7 : 0.2}
          />
        </mesh>

        {/* Ánh sáng phát ra khi hover - Sáng rõ */}
        {hovered && !showFeedback && (
          <pointLight 
            position={[2.4, 0, 0.5]} 
            color="#fbbf24" 
            intensity={2} 
            distance={8} 
            decay={2}
          />
        )}

        {/* Tay nắm cửa - offset để ở bên phải cửa */}
        <mesh position={[4.3, 0, 0.12]}>
          <cylinderGeometry args={[0.09, 0.09, 0.28, 16]} />
          <meshStandardMaterial color="#fbbf24" metalness={0.9} roughness={0.1} />
        </mesh>
        <mesh position={[4.45, 0, 0.12]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.09, 0.09, 0.22, 16]} />
          <meshStandardMaterial color="#fbbf24" metalness={0.9} roughness={0.1} />
        </mesh>

        {/* Đáp án text - ở phần trên cửa để không che nhân vật */}
        <Html
          position={[2.4, 1.8, 0.12]}
          center
          transform
          occlude
          distanceFactor={10}
          zIndexRange={[0, 0]}
        >
        <div
          onClick={!showFeedback ? onClick : undefined}
          onMouseEnter={() => !showFeedback && setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            color: 'white',
            fontSize: label.length > 40 ? '24px' : label.length > 25 ? '27px' : label.length > 15 ? '30px' : '32px',
            fontWeight: 'bold',
            textAlign: 'center',
            width: '360px',
            maxWidth: '360px',
            minHeight: '40px',
            maxHeight: '240px',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            wordWrap: 'break-word',
            whiteSpace: 'normal',
            lineHeight: '1.25',
            textShadow: '0 0 20px rgba(0,0,0,1), 0 0 40px rgba(0,0,0,0.95), 0 4px 8px rgba(0,0,0,1)',
            WebkitTextStroke: '2px rgba(0,0,0,0.8)',
            letterSpacing: '0.2px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px 20px',
            boxSizing: 'border-box',
            cursor: showFeedback ? 'default' : 'pointer',
            userSelect: 'none'
          }}
        >
          {label}
        </div>
      </Html>

        {/* Icon feedback - dưới cửa */}
        {showFeedback && isSelected && (
          <Html
            position={[2.4, -3.8, 0.15]}
            center
            transform
            occlude
            style={{ pointerEvents: 'none' }}
          >
            <div
              style={{
                fontSize: '56px',
                color: isCorrect ? '#10b981' : '#ef4444',
                textShadow: '0 0 20px currentColor',
                fontWeight: 'bold'
              }}
            >
              {isCorrect ? '✓' : '✗'}
            </div>
          </Html>
        )}
      </group>
    </group>
  )
}

interface DarkRoomSceneProps {
  answers: string[]
  selectedDoor: number | null
  correctIndex: number
  showFeedback: boolean
  onDoorClick: (index: number) => void
  question: string
  isCorrect: boolean | null
  isTransitioning?: boolean
  roomOffset?: number
}

/**
 * Component chân dung nhà vật lý - Nhỏ, không bị che
 */
const PhysicistPortrait: React.FC<{ position: [number, number, number], name: string, imageUrl: string, rotation?: [number, number, number] }> = ({ position, name, imageUrl, rotation = [0, 0, 0] }) => {
  return (
    <group position={position} rotation={rotation}>
      {/* Khung ảnh vàng kim - NHỎ */}
      <mesh>
        <boxGeometry args={[1.2, 1.6, 0.06]} />
        <meshStandardMaterial color="#b8860b" metalness={0.9} roughness={0.2} emissive="#daa520" emissiveIntensity={0.3} />
      </mesh>
      
      {/* Ảnh thật từ Wikipedia - NHỎ */}
      <Html
        position={[0, 0, 0.05]}
        center
        transform
        style={{ pointerEvents: 'none' }}
      >
        <div style={{
          width: '110px',
          height: '140px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: '#1a1a1a',
          border: '3px solid #8b6914',
          borderRadius: '4px',
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)'
        }}>
          <img 
            src={imageUrl} 
            alt={name}
            style={{
              width: '100%',
              height: '100px',
              objectFit: 'cover',
              filter: 'sepia(0.2) contrast(1.2) brightness(1.1)'
            }}
            onError={(e) => {
              // Fallback nếu ảnh không load được
              (e.target as HTMLImageElement).style.display = 'none'
            }}
          />
          <div style={{
            color: '#ffd700',
            fontSize: '12px',
            fontWeight: 'bold',
            padding: '6px',
            textAlign: 'center',
            background: 'linear-gradient(to bottom, #2d2d2d, #1a1a1a)',
            width: '100%',
            textShadow: '0 1px 2px rgba(0,0,0,0.8)'
          }}>
            {name}
          </div>
        </div>
      </Html>
    </group>
  )
}

/**
 * Component công thức vật lý trên tường
 */
const PhysicsFormula: React.FC<{ position: [number, number, number], formula: string, rotation?: [number, number, number] }> = ({ position, formula, rotation = [0, 0, 0] }) => {
  return (
    <group position={position} rotation={rotation}>
      {/* Bảng công thức */}
      <mesh>
        <planeGeometry args={[2, 0.8]} />
        <meshStandardMaterial 
          color="#1a1a2e" 
          emissive="#2d2d4d" 
          emissiveIntensity={0.3}
        />
      </mesh>
      {/* Viền vàng */}
      <mesh position={[0, 0, 0.01]}>
        <boxGeometry args={[2.1, 0.9, 0.02]} />
        <meshStandardMaterial color="#b8860b" metalness={0.7} roughness={0.3} />
      </mesh>
      {/* Công thức */}
      <Html
        position={[0, 0, 0.05]}
        center
        transform
        style={{ pointerEvents: 'none' }}
      >
        <div style={{
          color: '#ffd700',
          fontSize: '22px',
          fontWeight: 'bold',
          fontFamily: 'monospace',
          textAlign: 'center',
          textShadow: '0 0 10px rgba(255,215,0,0.5)',
          padding: '8px 12px'
        }}>
          {formula}
        </div>
      </Html>
    </group>
  )
}

/**
 * Component dụng cụ vật lý - Lò xo
 */
const SpringEquipment: React.FC<{ position: [number, number, number] }> = ({ position }) => {
  const springRef = useRef<THREE.Group>(null)
  
  useFrame(({ clock }) => {
    if (springRef.current) {
      // Lò xo dao động nhẹ
      springRef.current.position.y = position[1] + Math.sin(clock.getElapsedTime() * 2) * 0.1
    }
  })
  
  return (
    <group ref={springRef} position={position}>
      {/* Trục treo */}
      <mesh position={[0, 0.8, 0]}>
        <cylinderGeometry args={[0.05, 0.05, 0.6, 8]} />
        <meshStandardMaterial color="#888888" metalness={0.8} roughness={0.2} />
      </mesh>
      {/* Vòng lò xo */}
      {[0, 0.15, 0.3, 0.45].map((y, i) => (
        <mesh key={i} position={[0, y - 0.2, 0]}>
          <torusGeometry args={[0.15, 0.03, 8, 16]} />
          <meshStandardMaterial color="#ff6b6b" metalness={0.6} roughness={0.4} />
        </mesh>
      ))}
      {/* Quả nặng */}
      <mesh position={[0, -0.5, 0]}>
        <sphereGeometry args={[0.12, 16, 16]} />
        <meshStandardMaterial color="#4a4a4a" metalness={0.8} roughness={0.3} />
      </mesh>
    </group>
  )
}

/**
 * Component con lắc đơn
 */
const PendulumEquipment: React.FC<{ position: [number, number, number] }> = ({ position }) => {
  const pendulumRef = useRef<THREE.Group>(null)
  
  useFrame(({ clock }) => {
    if (pendulumRef.current) {
      // Con lắc dao động
      pendulumRef.current.rotation.z = Math.sin(clock.getElapsedTime() * 1.5) * 0.4
    }
  })
  
  return (
    <group position={position}>
      <group ref={pendulumRef} position={[0, 0, 0]}>
        {/* Dây treo */}
        <mesh position={[0, -0.5, 0]}>
          <cylinderGeometry args={[0.02, 0.02, 1, 8]} />
          <meshStandardMaterial color="#666666" metalness={0.5} roughness={0.5} />
        </mesh>
        {/* Quả cầu */}
        <mesh position={[0, -1, 0]}>
          <sphereGeometry args={[0.15, 16, 16]} />
          <meshStandardMaterial color="#ffd700" metalness={0.9} roughness={0.1} />
        </mesh>
      </group>
      {/* Giá đỡ */}
      <mesh position={[0, 0.2, 0]}>
        <boxGeometry args={[0.4, 0.05, 0.1]} />
        <meshStandardMaterial color="#5a5a5a" metalness={0.6} roughness={0.4} />
      </mesh>
    </group>
  )
}

/**
 * Component bàn thí nghiệm với dụng cụ
 */
const LabTable: React.FC<{ position: [number, number, number] }> = ({ position }) => {
  return (
    <group position={position}>
      {/* Mặt bàn */}
      <mesh position={[0, 0, 0]}>
        <boxGeometry args={[1.5, 0.08, 0.8]} />
        <meshStandardMaterial color="#8b7355" metalness={0.2} roughness={0.7} />
      </mesh>
      {/* Chân bàn */}
      {[[-0.6, 0.4], [0.6, 0.4], [-0.6, -0.4], [0.6, -0.4]].map(([x, z], i) => (
        <mesh key={i} position={[x, -0.45, z]}>
          <cylinderGeometry args={[0.05, 0.05, 0.9, 8]} />
          <meshStandardMaterial color="#6b5d4f" metalness={0.3} roughness={0.6} />
        </mesh>
      ))}
      {/* Ống nghiệm */}
      <mesh position={[-0.4, 0.15, 0]}>
        <cylinderGeometry args={[0.04, 0.04, 0.25, 8]} />
        <meshStandardMaterial color="#87ceeb" metalness={0.5} roughness={0.1} transparent opacity={0.6} />
      </mesh>
      {/* Cốc đo */}
      <mesh position={[0.3, 0.12, 0.2]}>
        <cylinderGeometry args={[0.08, 0.06, 0.2, 8]} />
        <meshStandardMaterial color="#b8d4e8" metalness={0.4} roughness={0.2} transparent opacity={0.5} />
      </mesh>
    </group>
  )
}

/**
 * Component đèn chùm huyền bí với animation
 */
const MysticalChandelier: React.FC = () => {
  const chandelierRef = useRef<THREE.Group>(null)
  const gemsRef = useRef<THREE.Group[]>([])

  useFrame(({ clock }) => {
    const time = clock.getElapsedTime()
    
    // Đèn chùm xoay chậm
    if (chandelierRef.current) {
      chandelierRef.current.rotation.y = time * 0.2
      // Nhún nhẹ lên xuống
      chandelierRef.current.position.y = 11 + Math.sin(time * 0.8) * 0.15
    }

    // Các viên ngọc lấp lánh
    gemsRef.current.forEach((gem, i) => {
      if (gem) {
        const offset = i * 0.5
        gem.position.y = Math.sin(time * 2 + offset) * 0.1
        // @ts-ignore
        if (gem.children[0]?.material) {
          // @ts-ignore
          gem.children[0].material.emissiveIntensity = 1.5 + Math.sin(time * 3 + offset) * 0.5
        }
      }
    })
  })

  return (
    <group ref={chandelierRef} position={[0, 11, 1]}>
      {/* Dây treo */}
      <mesh position={[0, 0.8, 0]}>
        <cylinderGeometry args={[0.03, 0.03, 1.6, 8]} />
        <meshStandardMaterial color="#1a1a1a" metalness={0.8} roughness={0.2} />
      </mesh>
      
      {/* Khung đèn chính - Vàng kim */}
      <mesh>
        <sphereGeometry args={[0.6, 16, 16]} />
        <meshStandardMaterial 
          color="#ffd700" 
          metalness={0.9} 
          roughness={0.1}
          emissive="#ffed4e"
          emissiveIntensity={1.2}
          transparent
          opacity={0.8}
        />
      </mesh>

      {/* Ánh sáng từ đèn chùm - Trắng ấm */}
      <pointLight intensity={18} color="#fffaeb" distance={20} decay={2} />

      {/* Các viên ngọc xung quanh */}
      {[0, 60, 120, 180, 240, 300].map((angle, i) => {
        const rad = (angle * Math.PI) / 180
        return (
          <group 
            key={angle}
            // @ts-ignore
            ref={(el) => (gemsRef.current[i] = el)}
            position={[Math.cos(rad) * 0.8, 0, Math.sin(rad) * 0.8]}
          >
            <mesh>
              <sphereGeometry args={[0.12, 16, 16]} />
              <meshStandardMaterial 
                color="#fbbf24" 
                emissive="#fbbf24" 
                emissiveIntensity={2}
                metalness={1}
                roughness={0}
              />
            </mesh>
          </group>
        )
      })}
    </group>
  )
}

/**
 * Component tinh thể huyền bí với animation
 */
const MysticalCrystal: React.FC<{ position: [number, number, number] }> = ({ position }) => {
  const crystalRef = useRef<THREE.Mesh>(null)
  const lightRef = useRef<THREE.PointLight>(null)

  useFrame(({ clock }) => {
    const time = clock.getElapsedTime()
    
    // Xoay tinh thể
    if (crystalRef.current) {
      crystalRef.current.rotation.x = time * 0.5
      crystalRef.current.rotation.y = time * 0.8
      
      // Thay đổi cường độ phát sáng
      // @ts-ignore
      if (crystalRef.current.material) {
        // @ts-ignore
        crystalRef.current.material.emissiveIntensity = 1.5 + Math.sin(time * 2) * 0.5
      }
    }

    // Ánh sáng nhấp nháy
    if (lightRef.current) {
      lightRef.current.intensity = 4 + Math.sin(time * 3) * 2
    }
  })

  return (
    <group position={position}>
      <mesh ref={crystalRef}>
        <octahedronGeometry args={[0.25, 0]} />
        <meshStandardMaterial 
          color="#87ceeb" 
          emissive="#87ceeb" 
          emissiveIntensity={1.5}
          metalness={1}
          roughness={0}
          transparent
          opacity={0.85}
        />
      </mesh>
      <pointLight ref={lightRef} intensity={5} color="#87ceeb" distance={8} />
    </group>
  )
}

/**
 * Component sương mù động
 */
const AnimatedFog: React.FC = () => {
  const fogParticles = useRef<THREE.Mesh[]>([])
  
  // Tạo vị trí ngẫu nhiên cố định
  const positions = useMemo(() => 
    Array.from({ length: 20 }).map(() => ({
      x: (Math.random() - 0.5) * 25,
      y: Math.random() * 8 - 1,
      z: (Math.random() - 0.5) * 15,
      speedX: (Math.random() - 0.5) * 0.01,
      speedY: (Math.random() - 0.5) * 0.005,
      speedZ: (Math.random() - 0.01) * 0.01,
    })), []
  )

  useFrame(() => {
    fogParticles.current.forEach((particle, i) => {
      if (particle && positions[i]) {
        particle.position.x += positions[i].speedX
        particle.position.y += positions[i].speedY
        particle.position.z += positions[i].speedZ

        // Giới hạn phạm vi di chuyển
        if (Math.abs(particle.position.x) > 12.5) positions[i].speedX *= -1
        if (particle.position.y > 7 || particle.position.y < -1) positions[i].speedY *= -1
        if (Math.abs(particle.position.z) > 7.5) positions[i].speedZ *= -1
      }
    })
  })

  return (
    <group>
      {positions.map((pos, i) => (
        <mesh 
          key={`fog-${i}`} 
          position={[pos.x, pos.y, pos.z]}
          // @ts-ignore
          ref={(el) => (fogParticles.current[i] = el)}
        >
          <sphereGeometry args={[0.3, 8, 8]} />
          <meshBasicMaterial 
            color="#b8d4e8" 
            transparent 
            opacity={0.02}
          />
        </mesh>
      ))}
    </group>
  )
}

/**
 * Component cảnh 3D phòng tối với 3 cánh cửa
 */
// Danh sách nhà vật lý cho mỗi phòng - Sử dụng ảnh từ PUBLIC
const physicists = [
  [
    { name: 'Newton', img: '/images/scientists/12_isaac_newton.jpg' },
    { name: 'Galileo', img: '/images/scientists/06_galileo_galilei.jpg' },
    { name: 'Hooke', img: '/images/scientists/10_robert_hooke.jpg' }
  ],
  [
    { name: 'Einstein', img: '/images/scientists/28_albert_einstein.jpg' },
    { name: 'Bohr', img: '/images/scientists/30_niels_bohr.jpg' },
    { name: 'Planck', img: '/images/scientists/27_max_planck.jpg' }
  ],
  [
    { name: 'Faraday', img: '/images/scientists/20_michael_faraday.jpg' },
    { name: 'Maxwell', img: '/images/scientists/21_james_cleark_maxwell.jpg' },
    { name: 'Hertz', img: '/images/scientists/23_heinrich_hertz.jpg' }
  ],
  [
    { name: 'Marie Curie', img: '/images/scientists/25_marie_curie.jpg' },
    { name: 'Rutherford', img: '/images/scientists/29_ernest_rutherford.jpg' },
    { name: 'Thomson', img: '/images/scientists/26_jj_thomson.jpg' }
  ],
  [
    { name: 'Heisenberg', img: '/images/scientists/31_werner_heisenberg.jpg' },
    { name: 'Schrödinger', img: '/images/scientists/32_erwin_schrodinger.jpg' },
    { name: 'Dirac', img: '/images/scientists/33_paul_dirac.jpg' }
  ],
  [
    { name: 'Ohm', img: '/images/scientists/19_georg_simon_ohm.jpg' },
    { name: 'Ampère', img: '/images/scientists/18_andre_marie_ampere.jpg' },
    { name: 'Volta', img: '/images/scientists/16_alessandro_volta.jpg' }
  ],
  [
    { name: 'Kepler', img: '/images/scientists/07_johannes_kepler.jpg' },
    { name: 'Copernicus', img: '/images/scientists/05_nicolaus_copernicus.jpg' },
    { name: 'Archimedes', img: '/images/scientists/02_archimedes.jpg' }
  ],
  [
    { name: 'Feynman', img: '/images/scientists/35_richard_feynman.jpg' },
    { name: 'Hawking', img: '/images/scientists/37_stephen_hawking.jpg' },
    { name: 'Fermi', img: '/images/scientists/34_enrico_fermi.jpg' }
  ],
  [
    { name: 'Pascal', img: '/images/scientists/09_blaise_pascal.jpg' },
    { name: 'Bernoulli', img: '/images/scientists/13_daniel_bernoulli.jpg' },
    { name: 'Archimedes', img: '/images/scientists/02_archimedes.jpg' }
  ],
  [
    { name: 'Planck', img: '/images/scientists/27_max_planck.jpg' },
    { name: 'Boltzmann', img: '/images/scientists/22_ludwig_boltzmann.jpg' },
    { name: 'Hertz', img: '/images/scientists/23_heinrich_hertz.jpg' }
  ]
]

// Danh sách công thức cho mỗi phòng (2 công thức/phòng)
const formulas = [
  ['F = ma', 'E = mc²'],
  ['ω = 2π/T', 'T = 2π√(m/k)'],
  ['F = -kx', 'W = Fs'],
  ['v = s/t', 'a = Δv/Δt'],
  ['λ = v/f', 'P = VI'],
  ['Wt = ½kx²', 'Wđ = ½mv²'],
  ['T = 2π√(l/g)', 'f = 1/T'],
  ['E = hf', 'λ = h/p'],
  ['pV = nRT', 'Q = mcΔt'],
  ['v² = u²+2as', 'p = mv']
]

const DarkRoomScene3D: React.FC<DarkRoomSceneProps> = ({
  answers,
  selectedDoor,
  correctIndex,
  showFeedback,
  onDoorClick,
  question,
  isCorrect,
  isTransitioning = false,
  roomOffset = 0
}) => {
  const cameraRef = useRef<THREE.PerspectiveCamera>(null)
  
  // Xác định phòng hiện tại (từ roomOffset)
  const currentRoomIndex = Math.abs(Math.floor(roomOffset / 35))
  
  // Camera animation - Follow character MƯỢT MÀ qua cửa
  useFrame(({ camera }) => {
    if (isTransitioning) {
      // Camera theo CHẬM nhân vật - đi qua cửa mượt mà
      const targetZ = roomOffset - 18 // Camera tới cuối phòng
      camera.position.z = THREE.MathUtils.lerp(camera.position.z, targetZ, 0.025) // Chậm hơn
      camera.position.y = THREE.MathUtils.lerp(camera.position.y, 3.5, 0.02) // Chậm hơn
    } else {
      // Camera ở vị trí đầu phòng hiện tại
      const targetZ = roomOffset + 8
      const targetY = 0.5
      camera.position.z = THREE.MathUtils.lerp(camera.position.z, targetZ, 0.08)
      camera.position.y = THREE.MathUtils.lerp(camera.position.y, targetY, 0.08)
    }
  })
  
  // Play sound effects
  const playSound = (type: 'correct' | 'wrong' | 'door-open' | 'door-close') => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      
      if (type === 'correct') {
        // Âm thanh "Ta-da!" vui nhộn - Giai điệu chiến thắng C-E-G-C
        
        // Nốt 1: C5 - "Ta!"
        const osc1 = audioContext.createOscillator()
        const gain1 = audioContext.createGain()
        osc1.connect(gain1)
        gain1.connect(audioContext.destination)
        osc1.type = 'triangle'
        osc1.frequency.setValueAtTime(523.25, audioContext.currentTime) // C5
        gain1.gain.setValueAtTime(0.4, audioContext.currentTime)
        gain1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.12)
        osc1.start(audioContext.currentTime)
        osc1.stop(audioContext.currentTime + 0.12)
        
        // Nốt 2: E5 - "da!"
        const osc2 = audioContext.createOscillator()
        const gain2 = audioContext.createGain()
        osc2.connect(gain2)
        gain2.connect(audioContext.destination)
        osc2.type = 'triangle'
        osc2.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.08) // E5
        gain2.gain.setValueAtTime(0.4, audioContext.currentTime + 0.08)
        gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2)
        osc2.start(audioContext.currentTime + 0.08)
        osc2.stop(audioContext.currentTime + 0.2)
        
        // Nốt 3: G5 - Vui vẻ
        const osc3 = audioContext.createOscillator()
        const gain3 = audioContext.createGain()
        osc3.connect(gain3)
        gain3.connect(audioContext.destination)
        osc3.type = 'sine'
        osc3.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.16) // G5
        gain3.gain.setValueAtTime(0.35, audioContext.currentTime + 0.16)
        gain3.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.28)
        osc3.start(audioContext.currentTime + 0.16)
        osc3.stop(audioContext.currentTime + 0.28)
        
        // Nốt 4: C6 - Kết thúc cao vút "Woohoo!"
        const osc4 = audioContext.createOscillator()
        const gain4 = audioContext.createGain()
        osc4.connect(gain4)
        gain4.connect(audioContext.destination)
        osc4.type = 'sine'
        osc4.frequency.setValueAtTime(1046.50, audioContext.currentTime + 0.24) // C6
        gain4.gain.setValueAtTime(0.45, audioContext.currentTime + 0.24)
        gain4.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)
        osc4.start(audioContext.currentTime + 0.24)
        osc4.stop(audioContext.currentTime + 0.5)
        
        // Harmonic - Thêm sự rực rỡ
        const osc5 = audioContext.createOscillator()
        const gain5 = audioContext.createGain()
        osc5.connect(gain5)
        gain5.connect(audioContext.destination)
        osc5.type = 'sine'
        osc5.frequency.setValueAtTime(1568, audioContext.currentTime + 0.24) // G6 overtone
        gain5.gain.setValueAtTime(0.2, audioContext.currentTime + 0.24)
        gain5.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.45)
        osc5.start(audioContext.currentTime + 0.24)
        osc5.stop(audioContext.currentTime + 0.45)
      } else if (type === 'wrong') {
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)
        // Âm thanh sai: Nốt thấp buồn
        oscillator.type = 'triangle'
        oscillator.frequency.setValueAtTime(196, audioContext.currentTime) // G3
        oscillator.frequency.setValueAtTime(146.83, audioContext.currentTime + 0.2) // D3
        gainNode.gain.setValueAtTime(0.35, audioContext.currentTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)
        oscillator.start(audioContext.currentTime)
        oscillator.stop(audioContext.currentTime + 0.5)
      } else if (type === 'door-open') {
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)
        // Âm thanh cửa mở: Creak sound
        oscillator.type = 'sawtooth'
        oscillator.frequency.setValueAtTime(120, audioContext.currentTime)
        oscillator.frequency.exponentialRampToValueAtTime(80, audioContext.currentTime + 0.3)
        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3)
        oscillator.start(audioContext.currentTime)
        oscillator.stop(audioContext.currentTime + 0.3)
      } else if (type === 'door-close') {
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)
        // Âm thanh cửa đóng: Slam sound
        oscillator.type = 'square'
        oscillator.frequency.setValueAtTime(60, audioContext.currentTime)
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2)
        oscillator.start(audioContext.currentTime)
        oscillator.stop(audioContext.currentTime + 0.2)
      }
    } catch (error) {
      console.log('Audio not supported:', error)
    }
  }

  // Confetti effect
  const createConfetti = () => {
    const colors = ['#FFD700', '#FFA500', '#FF6347', '#FF69B4', '#00FF00', '#00BFFF', '#FF1493']
    const confettiContainer = document.createElement('div')
    confettiContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
    `
    document.body.appendChild(confettiContainer)

    // Tạo 100 mảnh pháo giấy
    for (let i = 0; i < 100; i++) {
      const confetti = document.createElement('div')
      const color = colors[Math.floor(Math.random() * colors.length)]
      const left = Math.random() * 100
      const animDuration = 2 + Math.random() * 2
      const size = 8 + Math.random() * 6
      
      confetti.style.cssText = `
        position: absolute;
        top: -20px;
        left: ${left}%;
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        opacity: 0.9;
        animation: confettiFall ${animDuration}s linear forwards;
        transform: rotate(${Math.random() * 360}deg);
        border-radius: ${Math.random() > 0.5 ? '50%' : '0'};
      `
      confettiContainer.appendChild(confetti)
    }

    // Animation CSS
    if (!document.getElementById('confetti-style')) {
      const style = document.createElement('style')
      style.id = 'confetti-style'
      style.textContent = `
        @keyframes confettiFall {
          to {
            transform: translateY(100vh) rotate(${360 + Math.random() * 360}deg);
            opacity: 0;
          }
        }
      `
      document.head.appendChild(style)
    }

    // Xóa sau 4s
    setTimeout(() => {
      document.body.removeChild(confettiContainer)
    }, 4000)
  }

  // Play sounds when state changes
  React.useEffect(() => {
    if (showFeedback && isCorrect !== null) {
      if (isCorrect) {
        playSound('correct')
        createConfetti() // Hiệu ứng pháo giấy
        setTimeout(() => playSound('door-open'), 300)
      } else {
        playSound('wrong')
        setTimeout(() => playSound('door-close'), 300)
      }
    }
  }, [showFeedback, isCorrect])

  // Play question appear sound
  const playQuestionAppearSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      
      // "Whoosh" sound - Swoosh xuất hiện
      const osc1 = audioContext.createOscillator()
      const gain1 = audioContext.createGain()
      osc1.connect(gain1)
      gain1.connect(audioContext.destination)
      osc1.type = 'sine'
      osc1.frequency.setValueAtTime(1200, audioContext.currentTime)
      osc1.frequency.exponentialRampToValueAtTime(400, audioContext.currentTime + 0.15)
      gain1.gain.setValueAtTime(0.25, audioContext.currentTime)
      gain1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15)
      osc1.start(audioContext.currentTime)
      osc1.stop(audioContext.currentTime + 0.15)
      
      // "Ding" sound - Bell nhẹ
      const osc2 = audioContext.createOscillator()
      const gain2 = audioContext.createGain()
      osc2.connect(gain2)
      gain2.connect(audioContext.destination)
      osc2.type = 'sine'
      osc2.frequency.setValueAtTime(1568, audioContext.currentTime + 0.1) // G6
      gain2.gain.setValueAtTime(0.3, audioContext.currentTime + 0.1)
      gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4)
      osc2.start(audioContext.currentTime + 0.1)
      osc2.stop(audioContext.currentTime + 0.4)
      
      // Harmonic
      const osc3 = audioContext.createOscillator()
      const gain3 = audioContext.createGain()
      osc3.connect(gain3)
      gain3.connect(audioContext.destination)
      osc3.type = 'triangle'
      osc3.frequency.setValueAtTime(2349, audioContext.currentTime + 0.1) // D7
      gain3.gain.setValueAtTime(0.15, audioContext.currentTime + 0.1)
      gain3.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.35)
      osc3.start(audioContext.currentTime + 0.1)
      osc3.stop(audioContext.currentTime + 0.35)
    } catch (error) {
      console.log('Audio not supported:', error)
    }
  }

  // Animation cho banner câu hỏi
  const bannerRef = React.useRef<THREE.Group>(null)
  const [animProgress, setAnimProgress] = React.useState(0)
  const animFrameRef = React.useRef<number | null>(null)
  const hasPlayedSound = React.useRef(false)

  React.useEffect(() => {
    // Cleanup previous animation
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current)
      animFrameRef.current = null
    }
    
    // Reset animation
    setAnimProgress(0)
    hasPlayedSound.current = false
    
    // Delay to ensure cleanup happened
    const timeoutId = setTimeout(() => {
      // Play sound only once
      if (!hasPlayedSound.current) {
        playQuestionAppearSound()
        hasPlayedSound.current = true
      }
      
      // Animate from 0 to 1 over 0.4s
      const startTime = Date.now()
      const duration = 400 // ms
      
      const animate = () => {
        const elapsed = Date.now() - startTime
        const progress = Math.min(elapsed / duration, 1)
        setAnimProgress(progress)
        
        if (progress < 1) {
          animFrameRef.current = requestAnimationFrame(animate)
        } else {
          animFrameRef.current = null
        }
      }
      
      animFrameRef.current = requestAnimationFrame(animate)
    }, 10)
    
    // Cleanup on unmount or question change
    return () => {
      clearTimeout(timeoutId)
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current)
        animFrameRef.current = null
      }
    }
  }, [question])

  useFrame(() => {
    if (bannerRef.current) {
      // Easing function (ease-out cubic)
      const eased = 1 - Math.pow(1 - animProgress, 3)
      
      // Scale: 0.3 -> 1
      bannerRef.current.scale.setScalar(0.3 + eased * 0.7)
      
      // Position Y: 9 -> 7 (drop down)
      bannerRef.current.position.y = 9 - eased * 2
      
      // Opacity effect on emissive
      const emissiveIntensity = 0.3 + (1 - eased) * 0.7
      bannerRef.current.traverse((child: any) => {
        if (child.material && child.material.emissive) {
          child.material.emissiveIntensity = emissiveIntensity
        }
      })
    }
  })

  return (
    <>
      {/* Banner câu hỏi 3D với animation */}
      <group ref={bannerRef} position={[0, 7, roomOffset - 3.3]}>
        {/* Nền banner */}
        <mesh>
          <planeGeometry args={[19, 2.4]} />
          <meshStandardMaterial 
            color="#5b21b6"
            opacity={0.95}
            transparent
            emissive="#7c3aed"
            emissiveIntensity={0.3}
          />
        </mesh>

        {/* Viền trên */}
        <mesh position={[0, 1.25, 0.01]}>
          <boxGeometry args={[19, 0.12, 0.02]} />
          <meshStandardMaterial color="#fbbf24" emissive="#fbbf24" emissiveIntensity={0.5} />
        </mesh>

        {/* Viền dưới */}
        <mesh position={[0, -1.25, 0.01]}>
          <boxGeometry args={[19, 0.12, 0.02]} />
          <meshStandardMaterial color="#fbbf24" emissive="#fbbf24" emissiveIntensity={0.5} />
        </mesh>

        {/* Câu hỏi - HTML gắn với banner */}
        <Html
          position={[0, 0, 0.02]}
          center
          transform
          occlude
          style={{ pointerEvents: 'none' }}
        >
          <div
            style={{
              width: '900px',
              maxWidth: '88vw',
              background: 'transparent',
              textAlign: 'center'
            }}
          >
            <div
              style={{
                color: 'white',
                fontSize: '26px',
                fontWeight: 'bold',
                lineHeight: '1.4',
                textShadow: '0 2px 8px rgba(0,0,0,0.9)',
                padding: '0 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}
            >
              <span style={{ color: '#fbbf24', fontSize: '32px' }}>🔮</span>
              <span>{question}</span>
            </div>
          </div>
        </Html>
      </group>

      {/* ============ ÁNH SÁNG CHỈ PHÒNG HIỆN TẠI ============ */}
      
      {/* Ánh sáng môi trường - Giảm để tạo bóng tối */}
      <ambientLight intensity={0.3} />
      
      {/* Đèn chính CHỈ rọi phòng hiện tại */}
      <directionalLight 
        position={[0, 12, roomOffset - 15]} 
        intensity={2.5} 
        color="#fffaeb" 
        castShadow 
      />
      
      {/* Ánh sáng rọi 3 cửa - Chỉ phòng hiện tại */}
      <pointLight position={[-7.5, 4, roomOffset - 3]} intensity={12} color="#ffd700" />
      <pointLight position={[0, 4, roomOffset - 3]} intensity={12} color="#ffd700" />
      <pointLight position={[7.5, 4, roomOffset - 3]} intensity={12} color="#ffd700" />

      {/* Ánh sáng chiếu nhân vật - Phòng hiện tại */}
      <pointLight position={[0, 3, roomOffset + 6]} intensity={10} color="#fffaeb" />
      
      {/* Ánh sáng giữa phòng - Chỉ phòng hiện tại */}
      <pointLight position={[0, 5, roomOffset - 18]} intensity={15} color="#fffaeb" distance={20} decay={2} />

      {/* ============ PHÒNG KÍN HUYỀN BÍ ============ */}
      
      {/* ============ PHÒNG DÀI (HÀNH LANG) ============ */}
      
      {/* Render nhiều phòng liên tiếp */}
      {[0, -35, -70, -105, -140, -175, -210, -245, -280, -315, -350].map((offset) => (
        <group key={`room-${offset}`} position={[0, 0, offset]}>
          {/* Sàn nhà mỗi phòng - Gạch xám đậm, dài 35m (từ 0 đến -35) */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.8, -17.5]} receiveShadow>
            <planeGeometry args={[30, 35]} />
            <meshStandardMaterial 
              color="#6b5d4f" 
              metalness={0.4} 
              roughness={0.6}
              emissive="#4a3f35"
              emissiveIntensity={0.1}
            />
          </mesh>

          {/* Tường sau mỗi phòng - GẠCH TỐI MÀU (SÁT với cánh cửa) */}
          <mesh position={[0, 4, -4]} receiveShadow>
            <planeGeometry args={[30, 18]} />
            <meshStandardMaterial 
              color="#5a4a3a" 
              metalness={0.2} 
              roughness={0.9}
              emissive="#3d2f23"
              emissiveIntensity={0.1}
            />
          </mesh>

          {/* Viền trang trí tường sau - Trên (SÁT cửa) */}
          <mesh position={[0, 12.8, -3.95]}>
            <boxGeometry args={[30, 0.2, 0.1]} />
            <meshStandardMaterial color="#8b6914" emissive="#b8860b" emissiveIntensity={0.5} metalness={0.7} />
          </mesh>

          {/* Viền trang trí tường sau - Dưới (SÁT cửa) */}
          <mesh position={[0, -4.8, -3.95]}>
            <boxGeometry args={[30, 0.2, 0.1]} />
            <meshStandardMaterial color="#8b6914" emissive="#b8860b" emissiveIntensity={0.5} metalness={0.7} />
          </mesh>

          {/* Tường trái BÊN NGOÀI CỘT - GẠCH TỐI (từ z=2 đến z=-33) */}
          <mesh position={[-17, 4, -15.5]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
            <planeGeometry args={[35, 18]} />
            <meshStandardMaterial 
              color="#5a4a3a" 
              metalness={0.2} 
              roughness={0.9}
              emissive="#3d2f23"
              emissiveIntensity={0.1}
            />
          </mesh>

          {/* Tường phải BÊN NGOÀI CỘT - GẠCH TỐI (từ z=2 đến z=-33) */}
          <mesh position={[17, 4, -15.5]} rotation={[0, -Math.PI / 2, 0]} receiveShadow>
            <planeGeometry args={[35, 18]} />
            <meshStandardMaterial 
              color="#5a4a3a" 
              metalness={0.2} 
              roughness={0.9}
              emissive="#3d2f23"
              emissiveIntensity={0.1}
            />
          </mesh>

          {/* Trần nhà mỗi phòng dài - Gỗ tối */}
          <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 13, -17.5]} receiveShadow>
            <planeGeometry args={[30, 35]} />
            <meshStandardMaterial 
              color="#4a3828" 
              metalness={0.1} 
              roughness={0.8}
              emissive="#2d2118"
              emissiveIntensity={0.05}
            />
          </mesh>
        </group>
      ))}

      {/* 🖼️ ẢNH NHÀ VẬT LÝ - PHÍA TRÊN BANNER, KHÔNG CHỒNG LẤN */}
      {(() => {
        const currentRoomIndex = Math.abs(Math.floor(roomOffset / 35)) % physicists.length
        const currentPhysicists = physicists[currentRoomIndex]
        return (
          <>
            {/* BÊN TRÁI cột, PHÍA TRƯỚC banner (z lớn hơn) */}
            <PhysicistPortrait 
              position={[-10, 10.5, roomOffset - 3.2]} 
              name={currentPhysicists[0].name} 
              imageUrl={currentPhysicists[0].img}
              rotation={[0, 0, 0]} 
            />
            
            {/* BÊN PHẢI cột, PHÍA TRƯỚC banner (z lớn hơn) */}
            <PhysicistPortrait 
              position={[10, 10.5, roomOffset - 3.2]} 
              name={currentPhysicists[1].name} 
              imageUrl={currentPhysicists[1].img}
              rotation={[0, 0, 0]} 
            />
          </>
        )
      })()}

      {/* ============ TRANG TRÍ ============ */}
      
      {/* Cột trang trí ở đầu hành lang */}
      <group position={[-13, 4, roomOffset - 3]}>
        <mesh>
          <cylinderGeometry args={[0.4, 0.5, 12, 8]} />
          <meshStandardMaterial color="#e8dcc8" metalness={0.3} roughness={0.6} />
        </mesh>
        <mesh position={[0, 6, 0]}>
          <cylinderGeometry args={[0.6, 0.4, 0.8, 8]} />
          <meshStandardMaterial color="#daa520" metalness={0.8} roughness={0.3} emissive="#ffd700" emissiveIntensity={0.3} />
        </mesh>
      </group>

      <group position={[13, 4, roomOffset - 3]}>
        <mesh>
          <cylinderGeometry args={[0.4, 0.5, 12, 8]} />
          <meshStandardMaterial color="#e8dcc8" metalness={0.3} roughness={0.6} />
        </mesh>
        <mesh position={[0, 6, 0]}>
          <cylinderGeometry args={[0.6, 0.4, 0.8, 8]} />
          <meshStandardMaterial color="#daa520" metalness={0.8} roughness={0.3} emissive="#ffd700" emissiveIntensity={0.3} />
        </mesh>
      </group>

      {/* Nhân vật */}
      <Character 
        isMovingToDoor={showFeedback && isCorrect === true && !isTransitioning}
        isTransitioning={isTransitioning}
        targetDoorIndex={selectedDoor !== null ? selectedDoor : undefined}
        roomOffset={roomOffset}
      />

      {/* Ba cánh cửa - Tăng khoảng cách với banner */}
      {answers.map((answer, index) => (
        <Door
          key={index}
          position={[(index - 1) * 7.5, 1.5, roomOffset - 3.5] as [number, number, number]}
          label={answer}
          isSelected={selectedDoor === index}
          isCorrect={selectedDoor === index ? index === correctIndex : null}
          showFeedback={showFeedback}
          onClick={() => !showFeedback && onDoorClick(index)}
        />
      ))}

      {/* Điều khiển camera */}
      <OrbitControls
        enableZoom={true}
        minDistance={4}
        maxDistance={10}
        enablePan={false}
        minPolarAngle={Math.PI / 3.5}
        maxPolarAngle={Math.PI / 2.2}
        target={[0, 3.2, roomOffset - 2.5]}
      />
    </>
  )
}

/**
 * Component wrapper với Canvas - Fill toàn bộ không gian
 */
export const DarkRoomScene: React.FC<DarkRoomSceneProps> = (props) => {
  return (
    <div className="w-full h-full">
      <Suspense fallback={
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-purple-900 to-black text-white">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-xl">Đang tải phòng bí mật...</p>
          </div>
        </div>
      }>
        <Canvas
          camera={{ position: [0, 0.5, 6.5], fov: 90 }}
          shadows
          dpr={[1, 2]}
          gl={{ 
            antialias: true, 
            alpha: false,
            powerPreference: 'high-performance'
          }}
          style={{ background: 'linear-gradient(to bottom, #3d2f23, #2a1f19)', width: '100%', height: '100%' }}
        >
          <DarkRoomScene3D {...props} />
        </Canvas>
      </Suspense>
    </div>
  )
}

